import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { visuallyHidden } from "@mui/utils";
import {
  Button,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  SelectChangeEvent,
  Switch,
  TextField,
} from "@mui/material";
import { chunk, orderBy as odb } from "lodash";
import { ReactComponent as FilterSvg } from "assets/Svgs/filter.svg";
import CircularProgress from "@mui/material/CircularProgress";
import { ReactComponent as Csv } from "assets/Svgs/Csv.svg";
import { ReactComponent as PictureAsPdf } from "assets/Svgs/PictureAsPdf.svg";

import { useBoundStore } from "store/store";

import i18n from "i18n/i18n";
import { formatDate, formatDateCustom } from "shared-functions/formatDate";
import { ReactComponent as CachedIconWhite } from "assets/Svgs/CachedIconWhite.svg";
import _ from "lodash";
import moment from "moment";

interface ReportsTableProps {
  report: any; // Replace 'any' with the actual type of your 'report' prop
  reportType: any;
}
interface Data {
  id: number;
  Datum: string;
  Werk: string;
  Gerät: string;
  Actions: any;
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(
  order: Order | any,
  orderBy: Key | any
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
  width: string;
  display: string;
}

interface EnhancedTableProps {
  numSelected: number;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  reportType: string;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const lang = useBoundStore((state) => state.lang);

  const headCells: readonly HeadCell[] = [
    {
      id: "Datum",
      numeric: false,
      disablePadding: false,
      label: i18n(lang).date,
      width: "20%",
      display: "table-cell",
    },
    
    {
      id: "Actions",
      numeric: false,
      disablePadding: false,
      label: "",
      width: "20%",
      display: "table-cell",
    },
  ];

  const { order, orderBy, onRequestSort } = props;
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell, index) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
            className="tableHead"
            style={{ width: headCell.width, display: headCell.display }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
              sx={{ display: "flex", flexDirection: "row" }}
              IconComponent={FilterSvg}
            >
              {headCells.length - 1 === index ? "" : headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const ReportsTable: React.FC<ReportsTableProps> = (props) => {
  let nodeid = props.report?.reportconfiguration?.nodeid;
  const getAllReports = useBoundStore((state) => state.getAllReports);
  const getReports: any = useBoundStore((state) => state.getReports);
  const [reports, setReports] = React.useState<any>([]);
  const [all_reports, setAllReports] = React.useState<any>([]);

  const [timeRange, setTimeRange] = React.useState("");

  const executeReportConfiguration = useBoundStore(
    (state) => state.executeReportConfiguration
  );

  const executionLoader = useBoundStore((state) => state.executionLoader);

  const handleExecution = () => {
    executeReportConfiguration({
      reportConfigurationNodeId: props?.report?.reportconfiguration?.nodeid,
    });
  };

  React.useEffect(() => {
    if (nodeid || executionLoader === "loaded") {
      getAllReports(nodeid);
    }
  }, [nodeid, executionLoader]);

  React.useEffect(() => {
    if (getReports && getReports.result) {
      // Check if result is an array with at least one element
      if (getReports.result.length > 0) {
        // Check if data is an array with at least one element
        if (getReports.result[0].data && getReports.result[0].data.length > 0) {
          setReports(
            getReports?.result[0]?.data[0]?.reportconfiguration?.hasreport
          );
          setAllReports(
            getReports?.result[0]?.data[0]?.reportconfiguration?.hasreport
          );
        } else {
          console.error("Data array is empty or undefined");
        }
      } else {
        console.error("Result array is empty");
      }
    } else {
      console.error("getReports or result is undefined");
    }
  }, [getReports]);

  const subscribedEvents = useBoundStore((state) => state.subscribedEvents);

  React.useEffect(() => {
    
  }, [subscribedEvents]);

  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState<keyof Data>("id");
  const [selected, setSelected] = React.useState<readonly number[]>([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const [sorting, setSoring] = React.useState("alle");

  const [searchText, setSearchText] = React.useState("");

  const [searchData, setSearchData] = React.useState<Data[] | []>([]);

  function stableSort<T>(
    array: readonly T[],
    comparator: (a: T, b: T) => number
  ) {
    

    let sortedArray = odb(array, ["report.created"], [order]);
    return sortedArray;
  }

  const handleSortingChange = (event: SelectChangeEvent) => {
    setSoring(event.target.value as string);
  };

  function isNumeric(n: any) {
    return !isNaN(parseFloat(n)) && isFinite(n);
  }

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
   

    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = reports.map((n: any) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage - 1);
  };

  

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - reports.length) : 0;

 

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
    (window as any).blurAll();
  };

  let searchEnabled = searchText.length;

  let paginationchunks = chunk(
    searchEnabled ? searchData : reports,
    rowsPerPage
  );

  const handleZipDownload = async (csvUrl: any, fileName: any) => {
    try {
      // Fetch the CSV file from the provided URL
      const response = await fetch(csvUrl);
      const csvBlob = await response.blob();

      // Create an anchor element
      const link = document.createElement("a");

      // Set the href and download attributes
      link.href = URL.createObjectURL(csvBlob);
      link.download = fileName || "download.csv";

      // Simulate a click on the anchor element to trigger the download
      document.body.appendChild(link);
      link.click();

      // Remove the anchor element from the DOM
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading CSV:", error);
    }
  };

  const lang: any = useBoundStore((state) => state.lang);
  const updateReportConfigFlag = useBoundStore(
    (state) => state.updateReportConfigFlag
  );
  const isAuthUser = useBoundStore((state) => state.isAuthUser);
  const legalEntityNodeId = useBoundStore((state) => state.legalEntityNodeId);

  const getAllReportConfigurations = useBoundStore(
    (state) => state.getAllReportConfigurations
  );

  const handleSwitchToggle = async (e: any) => {
    try {
      const promises = await updateReportConfigFlag({
        isactive: e.target.checked,
        reportConfigurationNodeId: nodeid,
      });

      if (isAuthUser === true) {
        const result = await Promise.all(promises);

        getAllReportConfigurations(legalEntityNodeId);
       
      }
    } catch (error) {
      console.error("Error during promise execution:", error);
    }
  };

  React.useEffect(() => {
    const isAsc = sorting && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy("Datum");

    if (timeRange !== "") {
      let date = moment(timeRange).format("DD/MM/YYYY");
      setReports(
        all_reports.filter((report: any) => {
          return moment(report.report.created).format("DD/MM/YYYY") === date;
        })
      );
    }
  }, [sorting, timeRange]);

  const handleTimeRangeChange = (e: any) => {
    setTimeRange(e.target.value);
  };

  return (
    <>
      <div className="dashboardTableHeader reportTable">
        <div className="title">
          {" "}
          {i18n(lang).reports}
          <span>
            {props.reportType === "manual" ? (
              <Button
                variant="contained"
                style={{
                  backgroundColor: "var(--primary-color)",
                  marginLeft: "16px",
                }}
                startIcon={
                  executionLoader === "loading" ? (
                    <CircularProgress size={15} color="info" />
                  ) : (
                    <CachedIconWhite fill="#FFFFFF" />
                  )
                }
                onClick={handleExecution}
                disabled={executionLoader === "loading"}
              >
                {i18n(lang).create_now}
              </Button>
            ) : (
              <FormControlLabel
                control={
                  <Switch
                    defaultChecked={props.report.reportconfiguration.isactive}
                  />
                }
                onClick={handleSwitchToggle}
                label={i18n(lang).active}
                className="activeSwitch"
              />
            )}
          </span>
        </div>

        <div style={{ display: "flex" }}>
          <div style={{}}>
            <TextField
              id="date"
              label={i18n(lang)?.timerange}
              type="date"
              sx={{ minWidth: "180px", marginRight: "24px" }}
              variant="standard"
              InputLabelProps={{
                shrink: true,
              }}
              onChange={handleTimeRangeChange}
            />
          </div>
          <div>
            <FormControl fullWidth variant="standard">
              <InputLabel id="demo-simple-select-label">
                {i18n(lang)?.sorting}
              </InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={sorting}
                sx={{ minWidth: "180px" }}
                label={i18n(lang)?.sorting}
                onChange={handleSortingChange}
              >
                <MenuItem value={"alle"}>{i18n(lang).all}</MenuItem>
                <MenuItem value={"asc"}>{i18n(lang)?.ascending}</MenuItem>
                <MenuItem value={"desc"}>{i18n(lang)?.descending}</MenuItem>
              </Select>
            </FormControl>
          </div>
          
        </div>
      </div>

      <Table aria-labelledby="tableTitle" size="medium" className="reportTable">
        <EnhancedTableHead
          numSelected={selected.length}
          order={order}
          orderBy={orderBy}
          onSelectAllClick={handleSelectAllClick}
          onRequestSort={handleRequestSort}
          rowCount={searchEnabled ? searchData.length : reports.length}
          reportType={props.reportType}
        />

        <TableBody>
          {reports.length > 0
            ? stableSort(reports, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((data: any, index) => {
                  const labelId = `enhanced-table-checkbox-${index}`;
                  
                  return (
                    <TableRow
                      key={index}
                     
                    >
                      <TableCell
                        component="th"
                        scope="row"
                        style={{
                          color: "#000000DE",
                          width: "30%",
                          position: "relative",
                          padding: "10px 8px 10px 0px",
                        }}
                        id={labelId}
                      >
                        <div style={{ display: "flex", alignItems: "center" }}>
                        
                          <div className="reportDatum">
                            {data.report?.created &&
                              formatDateCustom(data.report.created)}{" "}
                            {/* {i18n(lang).time_h} */}
                          </div>
                        </div>
                      </TableCell>

                     

                      <TableCell align="right" sx={{ paddingRight: 0 }}>
                        <div style={{ display: "flex", justifyContent: "end" }}>
                          {data.report.csvreport ? (
                            <Csv
                              style={{ marginTop: "3px" }}
                              onClick={() =>
                                handleZipDownload(
                                  data.report.csvreport.url,
                                  data.report.csvreport.fileid
                                )
                              }
                            />
                          ) : (
                            <Csv />
                          )}

                          {data.report.pdfreport ? (
                            <PictureAsPdf
                              onClick={() =>
                                handleZipDownload(
                                  data.report.pdfreport.url,
                                  data.report.pdfreport.fileid
                                )
                              }
                              style={{ marginLeft: "14px" }}
                            />
                          ) : (
                            <PictureAsPdf style={{ marginLeft: "14px" }} />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
            : 
              ""}
          {emptyRows > 0 && (
            <TableRow
              style={{
                height: 53 * emptyRows,
              }}
            >
              <TableCell colSpan={5} />
            </TableRow>
          )}
        </TableBody>
      </Table>

      <div className="DashboardTablePagination">
        <div>
          {paginationchunks.length > 0 ? (
            <Pagination
              count={paginationchunks.length}
              shape="rounded"
              onChange={handleChangePage}
              hideNextButton={page === paginationchunks.length - 1}
              hidePrevButton={page === 0}
            />
          ) : (
            ""
          )}
        </div>
        <div>
          <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
            <Select
              labelId="demo-simple-select-standard-label"
              id="demo-simple-select-standard"
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              label={i18n(lang)?.rows_per_page}
              className="perPageSelect"
            >
              <MenuItem value={10 * 1}>
                {10}
                {i18n(lang)?.rows_per_page}
              </MenuItem>
              <MenuItem value={10 * 2}>
                {10 * 2}
                {i18n(lang)?.rows_per_page}
              </MenuItem>
              <MenuItem value={10 * 5}>
                {10 * 5}
                {i18n(lang)?.rows_per_page}
              </MenuItem>
              <MenuItem value={10 * 10}>
                {10 * 10}
                {i18n(lang)?.rows_per_page}
              </MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>
    </>
  );
};
export default ReportsTable;
